// src/pages/admin/WarrantyTransactionsAdmin.jsx
import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAdminOrders } from "../../api/orders";
import LoadingSpinner from "../../components/LoadingSpinner";
import { formatDateToDDMMYYYY } from "../../stores/utils";
import useDebounce from "../../stores/useDebounce";
import { toast } from "react-toastify";

export default function WarrantyTransactionsAdmin() {
  // ── Search & debounce ───────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // ── Pagination ──────────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const pageSize = 20;
  useEffect(() => setPage(1), [debouncedSearchTerm]);

  // ── Date range (max 30 days) ────────────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const [startDate, setStartDate] = useState(weekAgo);
  const [endDate, setEndDate] = useState(today);

  // ── Fetch data ──────────────────────────────────────────────────────
  const {
    data = { orders: [], totalPages: 0 },
    isLoading,
    isPreviousData,
  } = useQuery({
    queryKey: [
      "warrantyTransactions",
      { page, pageSize, search: debouncedSearchTerm, startDate, endDate },
    ],
    queryFn: () =>
      getAdminOrders({
        includeWarranty: true,
        page,
        pageSize,
        search: debouncedSearchTerm,
        startDate,
        endDate,
      }),
    keepPreviousData: true,
  });

  if (isLoading) return <LoadingSpinner />;
  const { orders, totalPages } = data;
  const noData = orders.length === 0;

  // ── Export CSV ──────────────────────────────────────────────────────
  async function exportToCsv() {
    // validate date range
    if (!startDate || !endDate) {
      return toast.error("Please select both start and end dates");
    }
    const a = new Date(startDate),
      b = new Date(endDate);
    const diffDays = (b - a) / (1000 * 60 * 60 * 24);
    if (diffDays < 0)
      return toast.error("End date must be on or after start date");
    if (diffDays > 30)
      return toast.error(
        "Date range cannot exceed 30 days, Kindly connect with Super Admin."
      );

    // fetch all matching
    const { orders: exportOrders = [] } = await getAdminOrders({
      includeWarranty: true,
      page: 1,
      pageSize: 100000,
      search: debouncedSearchTerm,
      startDate,
      endDate,
    });

    const headers = [
      "Order No.",
      "Registration No.",
      "Mileage",
      "Sale Price",
      "Term (months)",
      "Price (£)",
      "Per Repair (£)",
      "Warranty Start Date",
      "Customer Name",
      "Customer Email",
      "Customer Contact No.",
      "Customer Address",
    ];
    const rows = exportOrders.map((o) => [
      o.orderNumber,
      o.vehicle?.registration || "",
      o.vehicle?.mileage || 0,
      o.vehicle?.price || 0,
      o.warranty?.term ?? "",
      o.warranty?.price ?? "",
      o.warranty?.perRepairLimit ?? "",
      formatDateToDDMMYYYY(o.warranty?.startDate),
      `${o.firstName} ${o.lastName}`,
      o.email,
      o.contactNumber,
      `${o.address.street}, ${o.address.city}, ${o.address.postcode}, ${o.address.country}`,
    ]);

    const escape = (str) => `"${String(str).replace(/"/g, '""')}"`;
    const csvContent =
      "\uFEFF" +
      [headers, ...rows].map((r) => r.map(escape).join(",")).join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `warranty_${startDate}_to_${endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* ── Controls ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 mb-10">
        <div className="flex w-full mb-5">
          <div className="w-full">
            <h1 className="text-2xl font-bold">Warranty Transactions</h1>
          </div>
          {/* Search */}
          <input
            type="text"
            placeholder="Search order no., reg no., name, email…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="p-2 border rounded w-full"
          />
        </div>
        {/* Date Range and Export */}
        <div className="flex gap-3">
          {/* Date range */}
          <div className="flex items-center gap-2">
            <label className="text-sm">From</label>
            <input
              type="date"
              max={today}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="p-2 border rounded"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm">To</label>
            <input
              type="date"
              max={today}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="p-2 border rounded"
            />
          </div>

          {/* Export */}
          <button
            onClick={exportToCsv}
            disabled={noData}
            className="px-4 py-2 bg-accent text-white rounded hover:bg-accent/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* ── Empty State ──────────────────────────────────────────────── */}
      {noData ? (
        <div className="text-center py-20 text-gray-500">
          No warranty transactions found for the selected filters.
        </div>
      ) : (
        <>
          {/* ── Data Table ───────────────────────────────────────────────── */}
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-surface">
                <th className="p-2 text-left">Order No.</th>
                <th className="p-2 text-left">Registration No.</th>
                <th className="p-2 text-left">Mileage</th>
                <th className="p-2 text-left">Sale Price</th>
                <th className="p-2 text-left">Term (months)</th>
                <th className="p-2 text-left">Price (£)</th>
                <th className="p-2 text-left">Per Repair (£)</th>
                <th className="p-2 text-left">Warranty Start Date</th>
                <th className="p-2 text-left">Customer Name</th>
                <th className="p-2 text-left">Customer Email</th>
                <th className="p-2 text-left">Customer Address</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o._id} className="border-t">
                  <td className="p-2">{o.orderNumber}</td>
                  <td className="p-2">{o.vehicle?.registration}</td>
                  <td className="p-2">{o.vehicle?.mileage}</td>
                  <td className="p-2">{o.vehicle?.price || 0}</td>

                  <td className="p-2">{o.warranty?.term}</td>
                  <td className="p-2">£{o.warranty?.price}</td>
                  <td className="p-2">£{o.warranty?.perRepairLimit}</td>
                  <td className="p-2">
                    {formatDateToDDMMYYYY(o.warranty?.startDate)}
                  </td>
                  <td className="p-2">
                    {o.firstName} {o.lastName}
                  </td>
                  <td className="p-2">{o.email}</td>
                  <td className="p-2">
                    {`${o.address.street}, ${o.address.city}, ${o.address.postcode}, ${o.address.country}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ── Pagination ──────────────────────────────────────────────── */}
          <div className="flex justify-end items-center mt-4 space-x-4">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() =>
                setPage((p) => (!isPreviousData && p < totalPages ? p + 1 : p))
              }
              disabled={page === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
